import PropTypes from 'prop-types';
// @flow

import React from 'react';
import _ from 'lodash';
import { Constants } from 'constants';

import I18n from 'PMT/i18n/I18n';

import Listener from 'PMT/flux/Listener';

import Config from 'constants/Config';
import Events from 'constants/Events';
import ApiConstants from 'PMT/constants/ApiConstants';
import PMTViewConstants from 'PMT/constants/ViewConstants';

import ViewUtils from 'PMT/utils/ViewUtils';
import Utils from 'PMT/utils/Utils';

import KioskAction from 'actions/KioskAction';
import KioskSettingsStore from 'stores/KioskSettingsStore';

import { Restaurant } from 'PMT/entities';
import { Order, OrderSettings } from 'PMT/entities/order';
import KioskSettings from 'PMT/entities/restaurant/KioskSettings';
import RestaurantRoom from 'PMT/entities/restaurant/RestaurantRoom';
import KioskOrderTicketSettings from 'PMT/entities/restaurant/KioskOrderTicketSettings';

import SecurityContext from 'utils/permissions/SecurityContext';

//
// components
//
import AbstractOrderSettingsForm from '../components/AbstractOrderSettingsForm';
import Switch from 'react-bootstrap-switch';
import {
  LocaleChooser,
  OrderModeChooser,
  OptionsList,
} from 'PMT/components/form';
import { Form } from 'react-bootstrap';
import FormGroupView from 'PMT/components/form/FormGroupView';
import ImageDropzone from 'PMT/components/form/ImageDropzone';
import KioskTicketView from './KioskTicketView';
import IrlPaymentAllowedView from '../modes/components/IrlPaymentAllowedView';

export default class KioskSettingsPage extends AbstractOrderSettingsForm {

  static propTypes = {
    restaurant: PropTypes.objectOf(Restaurant),

    orderSettings: PropTypes.objectOf(OrderSettings),

    onSubmit: PropTypes.func.isRequired,
  };

  state = this.getDefaultState(this.props.orderSettings);

  componentDidMount() {
    KioskSettingsStore.addListener(this.onStateChange);

    this.loadSettings();
  }

  componentWillUnmount() {
    KioskSettingsStore.removeListener(this.onStateChange);
  }

  get defaultKioskSettings(): KioskSettings {
    return KioskSettingsStore.getSettings();
  }

  get kioskSettings(): KioskSettings {
    return this.state.kioskSettings;
  }

  loadSettings() {
    this.setState({
      loading: true,
    }, () => {
      KioskAction.loadSettings();
    });
  }

  //
  //
  //

  haveModifications(): boolean {
    return !(
      this.defaultKioskSettings.equals(this.kioskSettings)
      && _.isNull(this.state.backgroundImage)
      && _.isNull(this.state.advertisingImage)
    );
  }

  //
  //
  //

  getDefaultState(orderSettings: OrderSettings): Object {
    return {
      orderSettings: orderSettings.copy(),
      kioskSettings: this.defaultKioskSettings,
      backgroundImage: null,
      advertisingImage: null,
    };
  }

  isReadOnly(): boolean {
    return !SecurityContext.orderSettings.canEdit();
  }

  //
  //
  //

  onStateChange = () => {
    this.setState({
      kioskSettings: KioskSettingsStore.getSettings(),
      apiError: KioskSettingsStore.getApiError(),
      loading: false,
    });
  };

  //
  //
  //

  onSubmit = () => {
    if (!this.canEditSettings()) {
      return;
    }

    const backgroundImageData = !_.isNull(this.state.backgroundImage) ?
      ViewUtils.dataUriToFile(this.state.backgroundImage) : null;

    const advertisingImageData = !_.isNull(this.state.advertisingImage) ?
        ViewUtils.dataUriToFile(this.state.advertisingImage) : null;

    const files = {
      'background-image': backgroundImageData,
      'advertising-image': advertisingImageData,
    };

    this.setState({
      loading: true,
    }, () => {
      KioskAction.updateSettings(this.kioskSettings, files);
    });
  };

  onChangeOrderSettings = (orderSettings: OrderSettings) => {
    this.setState({
      orderSettings
    });
  };

  onBackgroundImageChange = (backgroundImage: DataUri) => {
    this.setState({
      backgroundImage,
    });
  };

  onAdvertisingImageChange = (advertisingImage: DataUri) => {
    this.setState({
      advertisingImage,
    });
  };

  onOrderModesChange = (modes: Array<OrderMode>) => {
    const kioskSettings = this.state.kioskSettings;

    kioskSettings.order.modes = modes;

    this.setState({
      kioskSettings
    });
  };

  onLanguagesChange = (locales: Array<Locale>) => {
    const kioskSettings = this.state.kioskSettings;

    kioskSettings.order.locales = locales;

    this.setState({
      kioskSettings
    });
  };

  onServiceAtTableEnabledChange = () => {
    const kioskSettings = this.state.kioskSettings;

    kioskSettings.order.serviceAtTableEnabled = !kioskSettings.order.serviceAtTableEnabled;

    this.setState({
      kioskSettings
    });
  };

  onIrlPaymentAllowedChange = () => {
    const kioskSettings = this.state.kioskSettings;

    kioskSettings.order.irlPaymentAllowed = !kioskSettings.order.irlPaymentAllowed;

    this.setState({
      kioskSettings
    });
  };

  onRoomsChange = (roomsOptions: SelectOptions) => {
    const kioskSettings = this.state.kioskSettings;

    const rooms = roomsOptions.map((option: SelectOption) => {
      let room;
      if (!_.isNull(option.value)) {
        room = option.value;
      } else {
        // create a new room.
        room = new RestaurantRoom();
        room.id = Utils.generateUID();
      }
      room.name = option.label;
      return room;
    });

    kioskSettings.order.rooms = rooms;

    this.setState({
      kioskSettings
    });
  };

  onTicketSettingsChange = (ticketSettings: KioskOrderTicketSettings) => {
    const kioskSettings = this.state.kioskSettings;

    kioskSettings.order.ticketSettings = ticketSettings;

    this.setState({
      kioskSettings
    });
  };

  //
  // Renderers
  //

  renderBackgroundImage() {
    const placeholder = _.isEmpty(this.kioskSettings.order.backgroundImage) ? null : this.kioskSettings.order.backgroundImage;

    return (
      <FormGroupView>
        <FormGroupView.Label>
          {I18n.tr('order.settings.kiosk.background')}
        </FormGroupView.Label>
        <FormGroupView.Content>
          <ImageDropzone
            placeholder={placeholder}
            image={this.state.backgroundImage}
            onChange={this.onBackgroundImageChange}
            aspectRatio={ApiConstants.Kiosk.Order.BackgroundImage.RATIO}
            width={ApiConstants.Kiosk.Order.BackgroundImage.WIDTH}
            height={ApiConstants.Kiosk.Order.BackgroundImage.HEIGHT}
            maxSize={ApiConstants.Kiosk.Order.BackgroundImage.MAX_SIZE}
            readOnly={this.isReadOnly()}
          />
        </FormGroupView.Content>
      </FormGroupView>
    );
  }

  renderAdvertisingImage() {
    const placeholder = _.isEmpty(this.kioskSettings.order.advertisingImage) ? null : this.kioskSettings.order.advertisingImage;

    return (
      <FormGroupView>
        <FormGroupView.Label>
          {I18n.tr('order.settings.kiosk.advertising')}
        </FormGroupView.Label>
        <FormGroupView.Content>
          <ImageDropzone
            placeholder={placeholder}
            image={this.state.advertisingImage}
            onChange={this.onAdvertisingImageChange}
            aspectRatio={ApiConstants.Kiosk.Order.AdvertisingImage.RATIO}
            width={ApiConstants.Kiosk.Order.AdvertisingImage.WIDTH}
            height={ApiConstants.Kiosk.Order.AdvertisingImage.HEIGHT}
            maxSize={ApiConstants.Kiosk.Order.AdvertisingImage.MAX_SIZE}
            readOnly={this.isReadOnly()}
          />
        </FormGroupView.Content>
      </FormGroupView>
    );
  }

  renderOrderModes() {
    const modes = [
      Order.OrderMode.ON_SITE,
      Order.OrderMode.TAKE_AWAY,
    ];

    return (
      <FormGroupView>
        <FormGroupView.Label>
          {I18n.tr('order.settings.kiosk.modes')}
        </FormGroupView.Label>
        <FormGroupView.Content>
          <OrderModeChooser
            modes={modes}
            readOnly={this.isReadOnly()}
            multi
            onChange={this.onOrderModesChange}
            values={this.state.kioskSettings.order.modes}
          />
        </FormGroupView.Content>
      </FormGroupView>
    );
  }

  renderLanguages() {
    return (
      <FormGroupView>
        <FormGroupView.Label>
          {I18n.tr('order.settings.kiosk.languages')}
        </FormGroupView.Label>
        <FormGroupView.Content>
          <LocaleChooser
              locales={
                Constants.localesForTranslation
                .map(l => l.locale.replace('-', '_'))
                .filter(
                    s => PMTViewConstants.KIOSK_ORDER_LOCALES().includes(s))}
            readOnly={this.isReadOnly()}
            multi
            onChange={this.onLanguagesChange}
            values={this.state.kioskSettings.order.locales}
          />
        </FormGroupView.Content>
      </FormGroupView>
    );
  }

  renderIrlPaymentAllowed() {
    return (
      <IrlPaymentAllowedView
        enabled={this.state.kioskSettings.order.irlPaymentAllowed}
        onChange={this.onIrlPaymentAllowedChange}
        readOnly={!this.canEditSettings()}
      />
    );
  }

  renderServiceAtTableEnabled() {
    return (
      <FormGroupView>
        <FormGroupView.Label>
          {I18n.tr('order.settings.kiosk.service_at_table_enabled')}
        </FormGroupView.Label>
        <FormGroupView.Content>
          <Switch
            bsSize="mini"
            disabled={this.isReadOnly()}
            onChange={this.onServiceAtTableEnabledChange}
            value={this.kioskSettings.order.serviceAtTableEnabled}
          />
        </FormGroupView.Content>
      </FormGroupView>
    );
  }

  renderRooms() {
    // TODO: user rooms service
    const list: Array<String> = this.state.kioskSettings.order.rooms.map((room: RestaurantRoom) => {
      return {
        label: room.name,
        value: room,
      };
    });
    return (
      <FormGroupView>
        <FormGroupView.Label>
          {I18n.tr('order.settings.kiosk.rooms')}
        </FormGroupView.Label>
        <FormGroupView.Content>
          <OptionsList
            list={list}
            onChange={this.onRoomsChange}
            addLabel={I18n.tr('global.add')}
            max={2}
            maxChars={RestaurantRoom.NAME_MAX_LENGTH}
          />
        </FormGroupView.Content>
      </FormGroupView>
    );
  }

  renderTicket() {
    return (
      <KioskTicketView
        restaurant={this.props.restaurant}
        orderSettings={this.state.orderSettings}
        ticketSettings={this.state.kioskSettings.order.ticketSettings}
        onChange={this.onTicketSettingsChange}
        readOnly={this.isReadOnly()}
      />
    );
  }

  renderBody() {
    return (
      <Form horizontal>
        {this.renderBackgroundImage()}
        {this.renderAdvertisingImage()}
        {this.renderOrderModes()}
        {this.renderLanguages()}
        {this.renderIrlPaymentAllowed()}
        {this.renderServiceAtTableEnabled()}
        {/* {this.renderRooms()} */}
        {this.renderTicket()}

        {this.renderFixedButtons()}
      </Form>
    );
  }

  render() {
    try {
      if (this.state.loading || _.isNull(this.state.kioskSettings)) {
        return this.renderLoading();
      }
      return this.renderBody();
    } catch(e) { console.error(e); }
  }

}



// WEBPACK FOOTER //
// ./scripts/pages/order/settings/kiosk/KioskSettingsPage.js